import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';

// ============================================================================
// Types
// ============================================================================

export type RateBasis = 'NET' | 'FULL';

export interface GoldRateFormData {
  rateDate: string;
  ratePerGram: string;
  rateBasis: RateBasis;
}

export interface GoldRateRecord {
  id: string;
  rateDate: string;
  ratePerGram: number;
  rateBasis: RateBasis;
  isActive: boolean;
  entityId: string;
  entityName: string;
  businessUnitId: string;
  businessUnitName: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
}

interface GoldRateFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: GoldRateFormData;
  onFormDataChange: (data: GoldRateFormData) => void;
  onSave: () => void;
  onClose: () => void;
}

interface GoldRateViewModalProps {
  isOpen: boolean;
  record: GoldRateRecord | null;
  onClose: () => void;
}

// ============================================================================
// Form Modal (Create / Edit) — Glassmorphism Design
// ============================================================================

export const GoldRateFormModal: React.FC<GoldRateFormModalProps> = ({
  isOpen,
  mode,
  formData,
  onFormDataChange,
  onSave,
  onClose,
}) => {
  if (!isOpen) return null;

  const title = mode === 'create' ? 'Add New Gold Rate' : 'Edit Gold Rate';

  const handleChange = (field: keyof GoldRateFormData, value: string) => {
    onFormDataChange({ ...formData, [field]: value });
  };

  // CR-002 guardrail: a Gross rate below ₹1,000/g or a Net rate above ₹1,000/g
  // usually means the wrong basis was picked.
  const rateValue = parseFloat(formData.ratePerGram) || 0;
  const basisWarning =
    rateValue > 0 &&
    ((formData.rateBasis === 'FULL' && rateValue < 1000) ||
      (formData.rateBasis === 'NET' && rateValue > 1000))
      ? formData.rateBasis === 'FULL'
        ? `₹${formData.ratePerGram}/g looks low for a Gross (full gold value) rate — did you mean the Net basis?`
        : `₹${formData.ratePerGram}/g looks high for a Net (headroom) rate — did you mean the Gross basis?`
      : null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group">
            <label className="form-label">Rate Date *</label>
            <input
              type="date"
              className="form-input"
              value={formData.rateDate}
              readOnly
              style={{ border: '1px solid #334155', backgroundColor: '#f1f5f9', cursor: 'not-allowed' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Rate Per Gram (INR) *</label>
            <input
              type="number"
              className="form-input"
              value={formData.ratePerGram}
              onChange={(e) => handleChange('ratePerGram', e.target.value)}
              placeholder="e.g., 6500.00"
              min={0}
              step="0.01"
              style={{ border: '1px solid #334155' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Rate Basis *</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', fontSize: '0.85rem' }}>
              <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <input
                  type="radio"
                  name="rateBasis"
                  checked={formData.rateBasis === 'FULL'}
                  onChange={() => handleChange('rateBasis', 'FULL')}
                  style={{ marginTop: '0.2rem' }}
                />
                <span>
                  <strong>Gross</strong> — full gold value per gram; eligibility deducts the outstanding GL amount
                </span>
              </label>
              <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '0.5rem', color: 'var(--text-secondary)' }}>
                <input
                  type="radio"
                  name="rateBasis"
                  checked={formData.rateBasis === 'NET'}
                  onChange={() => handleChange('rateBasis', 'NET')}
                  style={{ marginTop: '0.2rem' }}
                />
                <span>
                  <strong>Net</strong> — add-on headroom per gram, applied directly (legacy)
                </span>
              </label>
            </div>
            {basisWarning && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginTop: '0.5rem', padding: '0.5rem 0.75rem',
                background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '0.375rem', color: '#92400e', fontSize: '0.75rem' }}>
                <IconSystem.warning size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>{basisWarning}</span>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onSave}>
            <IconSystem.save size={16} />
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// View Modal — Glassmorphism Design
// ============================================================================

export const GoldRateViewModal: React.FC<GoldRateViewModalProps> = ({
  isOpen,
  record,
  onClose,
}) => {
  if (!isOpen || !record) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">Gold Rate Details</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div>
              <label className="form-label">Rate ID</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.id}
              </div>
            </div>
            <div>
              <label className="form-label">Rate Date</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {formatDate(record.rateDate)}
              </div>
            </div>
            <div>
              <label className="form-label">Rate Per Gram</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', fontWeight: 600 }}>
                {formatCurrency(record.ratePerGram)}
              </div>
            </div>
            <div>
              <label className="form-label">Rate Basis</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.rateBasis === 'FULL' ? 'Gross — full gold value' : 'Net — add-on headroom (legacy)'}
              </div>
            </div>
            <div>
              <label className="form-label">Status</label>
              <div style={{ padding: '0.5rem' }}>
                <span style={getStatusBadgeStyle(record.isActive ? 'ACTIVE' : 'INACTIVE')}>
                  {record.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div>
              <label className="form-label">Business Unit</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.businessUnitName}
              </div>
            </div>
            <div>
              <label className="form-label">Created By</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.createdByName || record.createdBy}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Created At</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {formatDate(record.createdAt)}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};
